import React, { useEffect, useMemo, useRef } from "react";
import Map from "../../components/map";
import Listview from "../../components/listview";
import Header from "../../components/header";
import {
    getGeoJSON,
    getAllProjects,
    getAllGrants,
    getSettings,
} from "../../actions/data";
import { selectGeoJSON, selectActivePage, selectMapType } from "../../reducers/";
import styles from "./root.module.scss";
import { connect } from "react-redux";
import ErrorBoundary from "./error-boundary";
import Loading from "../../components/loading";
import ProjectPopup from "../../components/project-popup";
import PropTypes from "prop-types";
import geojsonConfig from "../../modules/geojsonConfig";
import useComponentSize from "@rehooks/component-size";
import screenfull from "screenfull";
import { AriaLiveRegion } from "../../components/aria-live-region";

const ExpandIcon = props => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512"
        height={20}
        width={20}
        {...props}
    >
        <path
            fill="#000"
            d="M128 32H32C14.31 32 0 46.31 0 64v96c0 17.69 14.31 32 32 32s32-14.31 32-32V96h64c17.69 0 32-14.31 32-32s-14.3-32-32-32zm288 0h-96c-17.69 0-32 14.31-32 32s14.31 32 32 32h64v64c0 17.69 14.31 32 32 32s32-14.31 32-32V64c0-17.69-14.3-32-32-32zM128 416H64v-64c0-17.69-14.31-32-32-32S0 334.31 0 352v96c0 17.69 14.31 32 32 32h96c17.69 0 32-14.31 32-32s-14.3-32-32-32zm288-96c-17.69 0-32 14.31-32 32v64h-64c-17.69 0-32 14.31-32 32s14.31 32 32 32h96c17.69 0 32-14.31 32-32v-96c0-17.7-14.3-32-32-32z"
        />
    </svg>
);

const ContractIcon = props => (
    <svg
        height={20}
        width={20}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512"
        {...props}
    >
        <path
            fill="#000"
            d="M128 320H32c-17.69 0-32 14.31-32 32s14.31 32 32 32h64v64c0 17.69 14.31 32 32 32s32-14.31 32-32v-96c0-17.7-14.3-32-32-32zm288 0h-96c-17.69 0-32 14.31-32 32v96c0 17.69 14.31 32 32 32s32-14.31 32-32v-64h64c17.69 0 32-14.31 32-32s-14.3-32-32-32zm-96-128h96c17.69 0 32-14.31 32-32s-14.31-32-32-32h-64V64c0-17.69-14.31-32-32-32s-32 14.31-32 32v96c0 17.7 14.3 32 32 32zM128 32c-17.7 0-32 14.31-32 32v64H32c-17.69 0-32 14.3-32 32s14.31 32 32 32h96c17.69 0 32-14.31 32-32V64c0-17.69-14.3-32-32-32z"
        />
    </svg>
);

const Root = ({
    mapType,
    getAllProjects,
    getAllGrants,
    getSettings,
    activePage,
    geojson,
    getGeoJSON,
}) => {
    const ref = useRef();
    const size = useComponentSize(ref);

    const isMobile = useMemo(() => {
        if (size && size.width) {
            return size.width < 600;
        } else {
            return false;
        }
    }, [size]);

    useEffect(() => {
        getAllProjects();
        getAllGrants();
        getSettings();
        fetchGeoJson();
    }, [getAllProjects, getAllGrants, mapType]);

    function fetchGeoJson() {
        const currentConfig = geojsonConfig[mapType];
        getGeoJSON(currentConfig.geojsonUrl, currentConfig.topologyObjectsName);
    }

    if (!geojson) {
        return (
            <div data-container ref={ref} id="SMU_ROOT" className={styles.container}>
                <Loading />
            </div>
        );
    } else {
        return (
            <ErrorBoundary>
                <main className={styles.container} ref={ref} id="smu-crown-lands-main">
                    {size.height === 0 || size.width === 0 ? (
                        <Loading />
                    ) : (
                        <React.Fragment>
                            <Header
                                nameSelector={geojsonConfig[mapType].nameSelector}
                                isMobile={isMobile}
                                appHeight={size.height}
                            />
                            <ProjectPopup />
                            {activePage === "map" ? (
                                <Map
                                    isMobile={isMobile}
                                    width={size.width}
                                    height={size.height}
                                    idSelector={geojsonConfig[mapType].idSelector}
                                    idCol={geojsonConfig[mapType].idCol}
                                />
                            ) : (
                                <div style={{ width: size.width, height: size.height }}>
                                    <Listview />
                                </div>
                            )}
                            {!isMobile && (
                                <button
                                    onClick={() => {
                                        console.log("Going full screen");
                                        if (screenfull.isFullscreen) {
                                            screenfull.exit();
                                        }
                                        // Just fullscreen the element, not the whole tab
                                        else {
                                            screenfull.request(
                                                document.getElementById(
                                                    "smu-crown-lands-main"
                                                )
                                            );
                                        }
                                    }}
                                    title={
                                        screenfull.isFullscreen
                                            ? `Exit full screen`
                                            : `Enter full screen mode`
                                    }
                                    className={`${styles.fullScreenButton} mapboxgl-ctrl-group`}
                                >
                                    {screenfull.isFullscreen ? (
                                        <ContractIcon />
                                    ) : (
                                        <ExpandIcon width={20} height={20} />
                                    )}
                                </button>
                            )}
                        </React.Fragment>
                    )}
                    <AriaLiveRegion />
                </main>
            </ErrorBoundary>
        );
    }
};
const mapStateToProps = state => ({
    geojson: selectGeoJSON(state),
    activePage: selectActivePage(state),
    mapType: selectMapType(state),
});

Root.defaultProps = {
    mapType: "lga",
};

Root.propTypes = {
    getGeoJSON: PropTypes.func.isRequired,
    getAllProjects: PropTypes.func.isRequired,
    getAllGrants: PropTypes.func.isRequired,
    getSettings: PropTypes.func.isRequired,
    mapType: PropTypes.string.isRequired,
    activePage: PropTypes.string,
    geojson: PropTypes.object,
};
const mapDispatchToProps = { getGeoJSON, getAllProjects, getAllGrants, getSettings };
export default connect(mapStateToProps, mapDispatchToProps)(Root);



// WEBPACK FOOTER //
// ./src/pages/root/root.js